import { useEffect, useState } from 'react';
import { Avatar, Box, Modal, styled, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Input from 'components/Common/Input';
import { useForm } from 'react-hook-form';
import Button from 'components/Common/Button';
import { useNavigate, useParams } from 'react-router-dom';
import Select, { Option } from 'components/Common/Select';
import Spinner from 'components/Common/Spinner';
import { useCreateOrUpdateAgencyAgentTemplate, useGetAgencyAgentTemplate } from 'apis/agency/agentTemplate/hook';
import { useGetAgencyVoices } from 'apis/business/agent/voice/hook';
import { agentTemplateSchema } from 'utils/schema/validator/AgentTemplateValidation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetAgencyLanguages } from 'apis/agency/languages/hook';
import { titleCase } from 'utils/helperFunctions';

export default function AgentTemplateForm() {
  const { agentTemplateId } = useParams();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [voiceOptions, setVoiceOptions] = useState<Option[]>([]);
  const { data: agentTemplate, isLoading } = useGetAgencyAgentTemplate();

  const { data: voices, isLoading: isLoadingVoicesLoading } = useGetAgencyVoices();
  const { data: languages, isLoading: isLanguagesLoading } = useGetAgencyLanguages();

  const { handleSubmit, control } = useForm({
    resolver: zodResolver(agentTemplateSchema),
    values: agentTemplateId ? { agent_template: { ...agentTemplate } } : {}
  });

  useEffect(() => {
    if (voices?.data) {
      setVoiceOptions((prevOptions) => [
        ...prevOptions,
        ...voices.data.map((item) => ({
          label: item.friendly_name,
          value: item.voice_id,
          audioUrl: item.audio_url
        }))
      ]);
    }
  }, [voices]);

  const loadMoreVoices = () => {
    if (!isLoadingVoicesLoading && voices?.meta && currentPage < voices.meta.total_pages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const { mutate: handleCreateAgentTemplate } = useCreateOrUpdateAgencyAgentTemplate(true);
  const { mutate: handleUpdateAgentTemplate } = useCreateOrUpdateAgencyAgentTemplate();

  const onSubmit = (data?: any) => {
    handleCreateAgentTemplate(data);
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const formData = new FormData();
      formData.append('agent_template[avatar]', file);
      handleUpdateAgentTemplate(formData);
    }
  };

  if (isLoading || isLoadingVoicesLoading) {
    return <Spinner />;
  }

  return (
    <Modal
      open={true}
      onClose={() => navigate(-1)}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={style}>
        <Typography variant='h6'>{agentTemplateId ? 'Edit' : 'Add'} Agent Template</Typography>

        <Box component='form' onSubmit={handleSubmit(onSubmit)} mt={2}>
          {agentTemplateId && (
            <Box display={{ xs: 'block', sm: 'flex' }} alignItems='center' gap={2} marginTop='8px'>
              <Avatar src={agentTemplate?.avatar_url || ''} alt='User Avatar' sx={{ width: 64, height: 64 }} />
              <Box>
                <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Button component='label' role={undefined} variant='outlined' tabIndex={-1}>
                    Choose files
                    <VisuallyHiddenInput type='file' onChange={handleFileChange} multiple />
                  </Button>
                  <Typography variant='body2'>No file chosen</Typography>
                </Box>
                <Typography color='secondary' fontSize='12px'>
                  The proposed size is 350px*180px. No bigger than 2.5MB{' '}
                </Typography>
              </Box>
            </Box>
          )}
          <Grid container spacing='26px' marginTop={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Input name='agent_template.name' label='Name' control={control} fullWidth required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Input name='agent_template.agent_name' label='Agent Name' control={control} fullWidth required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Select
                label='Language'
                disabled={isLanguagesLoading}
                name='agent_template.languages'
                control={control}
                options={
                  languages?.data.map((item) => ({
                    label: titleCase(item),
                    value: item
                  })) || []
                }
                multiple
                required
              />
              <Typography color='secondary' lineHeight={1.5} fontSize={12}>
                The language that your agent understands should be the same language that it speaks
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Select
                name='agent_template.agent_interaction'
                label='Agent Interaction'
                control={control}
                options={[
                  { label: 'Inbounds', value: 'inbound' },
                  { label: 'Outbound', value: 'outbound' },
                  { label: 'Inbound and Outbound', value: ' inbound_and_outbound' }
                ]}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Select
                name='agent_template.industry'
                label='Industry'
                control={control}
                options={[
                  { label: 'Technology', value: 'technology' },
                  { label: 'Healthcare', value: 'healthcare' },
                  { label: 'Finance', value: 'finance' },
                  { label: 'Manufacturing', value: 'manufacturing' },
                  { label: 'Energy', value: 'energy' },
                  { label: 'Solar', value: 'solar' },
                  { label: 'Retail', value: 'retail' },
                  { label: 'Real Estate', value: 'real_estate' },
                  { label: 'Recruitment', value: 'recruitment' },
                  { label: 'Car Dealership', value: 'car_dealership' },
                  {
                    label: 'Transportation and logistics',
                    value: 'transportation_and_logistics'
                  },
                  { label: 'Mortgage', value: 'mortgage' },
                  { label: 'Hospitality', value: 'hospitality' },
                  { label: 'Insurance', value: 'insurance' },
                  { label: 'Agriculture', value: 'agriculture' },
                  {
                    label: 'Entertainment and Media',
                    value: 'entertainment_and_media'
                  },
                  { label: 'Education', value: 'education' },
                  {
                    label: 'Tourism and hospitality',
                    value: 'tourism_and_hospitality'
                  },
                  { label: 'Automotive', value: 'automotive' },
                  { label: 'Telecommunications', value: 'telecommunications' },
                  { label: 'Construction', value: 'construction' },
                  { label: 'Others', value: 'others' }
                ]}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Select
                label='Voice'
                name='agent_template.voice_id'
                isLoading={isLoadingVoicesLoading}
                control={control}
                options={voiceOptions}
                onScrollEnd={loadMoreVoices}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Select
                name='agent_template.agent_type'
                label='Agent Type'
                control={control}
                fullWidth
                options={[
                  { label: 'Customer Service', value: 'customer_service' },
                  { label: 'Receptionist', value: 'receptionist' },
                  { label: 'Order Taker', value: 'order_taker' },
                  { label: 'Tech Support', value: 'tech_support' },
                  { label: 'Reservationist', value: 'reservationist' },
                  { label: 'Frontdesk', value: 'frontdesk' }
                ]}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Select
                name='agent_template.tone'
                label='Tone'
                control={control}
                fullWidth
                options={[
                  { label: 'Professional', value: 'professional' },
                  { label: 'Conversational', value: 'conversational' },
                  { label: 'Humorous', value: 'humorous' },
                  { label: 'Empathic', value: 'empathic' },
                  { label: 'Simple', value: 'simple' },
                  { label: 'Academic', value: 'academic' },
                  { label: 'Creative', value: 'creative' },
                  { label: 'Friendly', value: 'friendly' }
                ]}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Input
                name='agent_template.goal'
                label='Goal'
                control={control}
                fullWidth
                required
                multiline
                minRows={4}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Input
                name='agent_template.background'
                label='Background'
                control={control}
                fullWidth
                required
                multiline
                minRows={4}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Input
                name='agent_template.welcome_message'
                label='Welcome Message'
                control={control}
                fullWidth
                required
                multiline
                minRows={4}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Input
                name='agent_template.description'
                label='Description'
                control={control}
                fullWidth
                required
                multiline
                minRows={4}
              />
            </Grid>
            {/* <Grid size={{ xs: 12, md: 6 }}>
              <Radio
                name='agent_template.physical_location_exists'
                label='Is Physical Location?'
                control={control}
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false }
                ]}
              />
            </Grid> */}
            <Grid size={12}>
              <Input
                name='agent_template.instructions'
                label='Instructions'
                control={control}
                fullWidth
                required
                multiline
                minRows={4}
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              gap: '16px',
              marginTop: '24px'
            }}
          >
            <Button
              color='secondary'
              variant='outlined'
              sx={{ border: '1px solid #F3F3F3' }}
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button variant='contained' type='submit'>
              {agentTemplateId ? 'Save' : 'Add'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  border: '1px solid #F3F3F3',
  boxShadow: 24,
  p: 2,
  borderRadius: '8px',
  width: {
    xs: '90%',
    sm: 400,
    md: 600,
    lg: 800
  },
  maxHeight: '90vh',
  overflow: 'auto'
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});
